'use client';

import { useState } from 'react';

import Link from 'next/link';

import { ChevronLeft, ChevronRight } from 'lucide-react';

import type { ServicesBlock } from '~/(tenant)/_lib/schema/landing-config.schema';

import { type LandingRenderContext, landingImageUrl, pickLocalized } from './landing-context';
import styles from './services-explorer.module.css';

export function ServicesExplorer({ block, ctx }: { block: ServicesBlock; ctx: LandingRenderContext }) {
  const [view, setView] = useState({ category: block.initialCategoryId ?? null, edges: { start: true, end: true } });
  const selectedCategory = view.category;
  const setSelectedCategory = (category: string | null) => setView((previous) => ({ ...previous, category }));
  const arabic = ctx.locale.startsWith('ar');
  const cards = block.mode === 'manual' ? block.cards ?? [] : ctx.services.map((service) => ({
    title: service.name,
    description: service.description,
    href: (block.autoLinkToBooking ?? true) ? '/book' : undefined,
  }));
  const categories = block.mode === 'manual' ? (block.categories ?? []).filter((category) => pickLocalized(category.label, category.label_ar, ctx.locale)?.trim()) : [];
  const activeCategory = categories.some((category) => category.id === selectedCategory) ? selectedCategory : null;
  const title = pickLocalized(block.title, block.title_ar, ctx.locale);
  const subtitle = pickLocalized(block.subtitle, block.subtitle_ar, ctx.locale);
  const eyebrow = pickLocalized(block.eyebrow, block.eyebrow_ar, ctx.locale);
  const visibleCards = filterExplorerCards(cards, activeCategory);
  const carousel = visibleCards.length > (block.columns ?? 3);
  const trackEdges = (track: HTMLElement) => {
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const edges = { start: position <= 2, end: position >= max - 2 };
    setView((previous) => previous.edges.start === edges.start && previous.edges.end === edges.end ? previous : { ...previous, edges });
  };
  const trackRef = (track: HTMLDivElement | null) => {
    if (!track || !carousel) return;
    trackEdges(track);
    const onResize = () => trackEdges(track);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  };
  const scrollTrack = (button: HTMLButtonElement, direction: 1 | -1) => {
    const track = button.closest('section')?.querySelector<HTMLElement>('[data-explorer-track]');
    const card = track?.firstElementChild;
    if (!track || !card) return;
    const step = card.getBoundingClientRect().width + (Number.parseFloat(getComputedStyle(track).columnGap) || 0);
    track.scrollBy({ left: direction * step * (arabic ? -1 : 1), behavior: 'smooth' });
  };

  return (
    <section className={styles.section} data-test="services-explorer" dir={arabic ? 'rtl' : undefined}>
      <div className={styles.inner}>
        <div className={styles.heading}>
          {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
          {title ? <h2>{title}</h2> : null}
          {subtitle ? <p className="landing-prose">{subtitle}</p> : null}
        </div>
        {categories.length ? (
          <div className={styles.filters} role="group" aria-label={arabic ? 'تصفية الأنشطة' : 'Filter activities'}>
            {explorerTabs<{ id: string | null; label: string; label_ar?: string }>(categories, { id: null, label: pickLocalized(block.allLabel, block.allLabel_ar, ctx.locale) || (arabic ? 'الكل' : 'All'), label_ar: undefined }, Boolean(block.initialCategoryId)).map((category) => (
              <button key={category.id ?? '__all'} type="button" data-sports-trigger aria-pressed={activeCategory === category.id} onClick={() => setSelectedCategory(category.id)}>
                {pickLocalized(category.label, category.label_ar, ctx.locale)}
              </button>
            ))}
          </div>
        ) : null}
        <div ref={trackRef} className={styles.cards} data-columns={block.columns ?? 3} data-carousel={carousel || undefined} data-explorer-track onScroll={carousel ? (event) => trackEdges(event.currentTarget) : undefined} key={activeCategory ?? '__all'}>
          {visibleCards.map((card, index) => {
            const cardTitle = pickLocalized(card.title, card.title_ar, ctx.locale);
            const description = pickLocalized(card.description, card.description_ar, ctx.locale);
            const content = (
              <>
                {card.imageKey ? (
                  <div className={styles.media}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={landingImageUrl(ctx, card.imageKey)} alt={cardTitle ?? ''} loading="lazy" />
                  </div>
                ) : null}
                <h3>{cardTitle}</h3>
                {description ? <p className="landing-prose">{description}</p> : null}
                {card.href ? <span className={styles.cta}>{pickLocalized(card.ctaLabel, card.ctaLabel_ar, ctx.locale) || (arabic ? 'اعرف المزيد' : 'Learn more')}</span> : null}
              </>
            );
            if (!card.href) return <article key={index} className={styles.card}>{content}</article>;
            return /^https?:\/\//i.test(card.href) ? (
              <a key={index} href={card.href} className={styles.card} target="_blank" rel="noopener noreferrer">{content}</a>
            ) : <Link key={index} href={card.href} className={styles.card}>{content}</Link>;
          })}
        </div>
        {carousel && !(view.edges.start && view.edges.end) ? (
          <div className={styles.pager}>
            <button type="button" data-sports-trigger aria-label={arabic ? 'السابق' : 'Previous'} disabled={view.edges.start} onClick={(event) => scrollTrack(event.currentTarget, -1)}><ChevronLeft size={20} aria-hidden="true" /></button>
            <button type="button" data-sports-trigger aria-label={arabic ? 'التالي' : 'Next'} disabled={view.edges.end} onClick={(event) => scrollTrack(event.currentTarget, 1)}><ChevronRight size={20} aria-hidden="true" /></button>
          </div>
        ) : null}
        {!visibleCards.length ? <p className={styles.empty} role="status">{arabic ? 'لا توجد أنشطة في هذه الفئة.' : 'No activities in this category.'}</p> : null}
      </div>
    </section>
  );
}

/** The "All" tab leads by default; it moves to the end when the block opens on a specific category. */
export function explorerTabs<T>(categories: T[], all: T, allLast: boolean): T[] {
  return allLast ? [...categories, all] : [all, ...categories];
}

export function filterExplorerCards(cards: NonNullable<ServicesBlock['cards']>, categoryId: string | null) {
  return categoryId ? cards.filter((card) => card.categoryIds?.includes(categoryId)) : cards;
}
